import { Box, Divider, InputAdornment } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { TextField } from 'src/components/new/inputs/TextField';
import { Typography } from 'src/components/new/Typography';
import { AppDispatch, RootState } from 'src/store/store';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { updateUnsavedMeteredInputs } from 'src/features/admin/adminSlice';
import { formatPrice, validateInput } from '../utils/utils';

interface MeteredInputProps {
  isEditingEnabled: boolean;
}
const MeteredInputs: React.FC<MeteredInputProps> = ({ isEditingEnabled }) => {
  const rates = useSelector((state: RootState) => state.admin.meteredRates);
  const unsavedMeteredInputs = useSelector((state: RootState) => state.admin.unsavedMeteredInputs);
  const dispatch = useDispatch<AppDispatch>();

  const onMeteredInputsChange = (e: React.ChangeEvent<HTMLInputElement>, inputName: 'paf_discount' | 'cert_err') => {
    const isValidInput = validateInput(e.target.value, 2);
    if (!isValidInput) return;
    const formattedPrice = formatPrice(e.target.value, 2);
    dispatch(updateUnsavedMeteredInputs({ name: inputName, value: formattedPrice }));
  };

  return (
    <Box
      sx={{
        border: '1px solid #dcdbdc',
        borderRadius: 2,
        paddingY: 2,
        paddingX: 1,
        width: '25rem',
        alignSelf: 'flex-start',
      }}
    >
      <Typography fontSize={18} fontWeight={600}>
        Metered Inputs
      </Typography>
      <Typography fontSize={14} fontWeight={500} sx={{ color: '#7e828b' }}>
        Key rates and discounts used in calculation
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
        <TextField
          name="pafDiscount"
          id="pafDiscount"
          value={unsavedMeteredInputs['paf_discount'] ?? `$${Number(rates.paf_discount).toFixed(2)}`}
          removeSpacer
          onChange={(e) => onMeteredInputsChange(e, 'paf_discount')}
          sx={{ backgroundColor: unsavedMeteredInputs['paf_discount'] ? '#ffeedf' : '#f6f6f6' }}
          label="PAF Discount"
          disabled={!isEditingEnabled}
          containerSX={{ flex: 3.5 }}
        />
        <Typography fontSize={13} fontWeight={500} sx={{ color: '#7e828b', mt: 2, flex: 1.5 }}>
          per domestic stamp
        </Typography>
      </Box>
      <Typography sx={{ color: '#7e828b', mt: 0.2 }} fontSize={13} fontWeight={500}>
        Pre-sorted/automation discount per domestic stamp
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3 }}>
        <TextField
          name="pafApplication"
          id="pafApplication"
          sx={{ backgroundColor: '#f6f6f6', borderColor: '#f6f6f6' }}
          value="3.00"
          label={
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <span
                style={{ fontSize: '0.875rem', fontWeight: 600, fontFamily: 'Instrument Sans', lineHeight: '1.25rem' }}
              >
                PAF Application
              </span>
              <LockOutlinedIcon fontSize="small" />
            </Box>
          }
          containerSX={{ flex: 3.5 }}
          removeSpacer
          disabled
        />
        <Typography fontSize={14} fontWeight={500} sx={{ color: '#7e828b', mt: 2, flex: 1.5 }}>
          oz or less
        </Typography>
      </Box>
      <TextField
        sx={{ backgroundColor: unsavedMeteredInputs['cert_err'] ? '#ffeedf' : '#f6f6f6' }}
        name="certERR"
        id="certERR"
        onChange={(e) => onMeteredInputsChange(e, 'cert_err')}
        value={unsavedMeteredInputs['cert_err'] ?? `$${Number(rates.cert_err).toFixed(2)}`}
        containerSX={{ mt: 2 }}
        label="Cert + ERR"
        disabled={!isEditingEnabled}
        removeSpacer
      />
      <Typography sx={{ color: '#7e828b', mt: 0.2 }} fontSize={13} fontWeight={500}>
        Certified Mail with Electronic Return Receipt fee
      </Typography>
      <Divider sx={{ mt: 3, mb: 1 }} />
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <InfoOutlinedIcon sx={{ display: 'inline-block', color: '#7e828b', fontSize: 14, mt: 0.5 }} />
        <Typography sx={{ color: '#7e828b', mt: 0.2 }} fontSize={14} fontWeight={500}>
          Changes to these inputs will automatically update all linked cells in the Certified Mail section
        </Typography>
      </Box>
    </Box>
  );
};

export default MeteredInputs;
